import React, { useState, useEffect } from "react";
import { useForm, useFormContext, useFieldArray, FormProvider } from "react-hook-form";
import { coursegroupsApi, CourseGroupApi, FaqItemApi } from "../../api/coursegroups/coursegroupsApi";
import { PaziaFromQuill } from "../../components/atoms/PaziaQuill/PaziaFromQuill";

interface FaqItemFormValues {
  question: string;
  answer: string;
}

interface FaqFormValues {
  items: FaqItemFormValues[];
}

interface FaqEditorProps {
  courseGroup: CourseGroupApi;
}

export const FaqEditor = ({ courseGroup }: FaqEditorProps) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set([0]));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FaqFormValues>({
    defaultValues: { items: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const mapApiToForm = (items: FaqItemApi[]): FaqItemFormValues[] =>
    items.map((item) => ({
      question: item.question,
      answer: item.answer,
    }));

  const loadFaq = async () => {
    try {
      const response = await coursegroupsApi.getFaq(courseGroup.id);
      form.reset({ items: mapApiToForm(response.data) });
      setExpandedItems(new Set([0]));
    } catch (err) {
      console.error("Error loading FAQ:", err);
      setError("Nie udało się załadować FAQ.");
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadFaq();
      setIsLoading(false);
    };
    init();
  }, [courseGroup.id]);

  const toggleItem = (index: number) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const values = form.getValues();
      const faq: FaqItemApi[] = values.items.map((item) => ({
        question: item.question,
        answer: item.answer,
      }));
      await coursegroupsApi.updateFaq(courseGroup.id, faq);
      form.reset(values);
    } catch (err) {
      setError("Nie udało się zapisać FAQ. Spróbuj ponownie.");
      console.error("Error saving FAQ:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const addItem = () => {
    append({ question: "", answer: "" });
    setExpandedItems((prev) => new Set([...prev, fields.length]));
  };

  const removeItem = (index: number) => {
    remove(index);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <div className="border-primary-500 h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"></div>
          <span>Ładowanie FAQ...</span>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100">FAQ</h3>
          <button
            type="button"
            onClick={addItem}
            className="bg-primary-500 hover:bg-primary-600 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-white transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Dodaj pytanie
          </button>
        </div>

        {/* Items List */}
        {fields.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center dark:border-slate-600 dark:bg-slate-700/50">
            <svg
              className="mx-auto mb-2 h-8 w-8 text-slate-400 dark:text-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-slate-500 dark:text-slate-400">Nie dodano jeszcze żadnych pytań</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">Kliknij "Dodaj pytanie" aby rozpocząć</p>
          </div>
        ) : (
          <div className="space-y-3">
            {fields.map((field, index) => (
              <FaqCard
                key={field.id}
                index={index}
                isExpanded={expandedItems.has(index)}
                onToggle={() => toggleItem(index)}
                onRemove={() => removeItem(index)}
              />
            ))}
          </div>
        )}

        {/* Save Button */}
        {fields.length > 0 && (
          <div className="flex justify-end border-t border-slate-200 pt-4 dark:border-slate-700">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !form.formState.isDirty}
              className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Zapisuję...
                </>
              ) : (
                "Zapisz zmiany"
              )}
            </button>
          </div>
        )}
      </div>
    </FormProvider>
  );
};

interface FaqCardProps {
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
}

const FaqCard = ({ index, isExpanded, onToggle, onRemove }: FaqCardProps) => {
  const { register, watch } = useFormContext<FaqFormValues>();

  const question = watch(`items.${index}.question`);

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      {/* Card Header */}
      <div
        className="flex cursor-pointer items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <svg
            className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? "rotate-90" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="font-medium text-slate-800 dark:text-slate-100">
            {question || `Pytanie ${index + 1}`}
          </span>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
          title="Usuń pytanie"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      {/* Card Content */}
      {isExpanded && (
        <div className="border-t border-slate-200 p-4 dark:border-slate-700">
          <div className="space-y-4">
            {/* Question */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Pytanie</label>
              <input
                type="text"
                {...register(`items.${index}.question`)}
                placeholder="Wprowadź pytanie..."
                className="form-input h-[2.375rem] w-full dark:bg-slate-700"
              />
            </div>

            {/* Answer (WYSIWYG) */}
            <PaziaFromQuill name={`items.${index}.answer`} label="Odpowiedź" />
          </div>
        </div>
      )}
    </div>
  );
};
